<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    import ActivityFeed, { type ActivityItem } from '../../components/ActivityFeed.svelte';
    import ConfirmModal from '../../components/ConfirmModal.svelte';
    import { Link, useForm, router } from '@inertiajs/svelte';
    import { topics } from '@/routes/admin';
    import { update, deleteMethod } from '@/routes/admin/topics';

    interface Topic {
        id: number;
        name: string;
        slug: string;
        disabled: boolean;
        not_for_credit: boolean;
        cases_count: number;
    }

    let { topic, activity = [] }: { topic: Topic; activity: ActivityItem[] } = $props();

    const form = useForm({
        name: topic.name,
        slug: topic.slug,
        disabled: topic.disabled,
        not_for_credit: topic.not_for_credit,
    });

    const slugChanged = $derived($form.slug !== topic.slug);

    let confirmingDelete = $state(false);
    let confirmingSlugChange = $state(false);

    function submit(e: Event) {
        e.preventDefault();
        if (slugChanged) {
            confirmingSlugChange = true;
        } else {
            doSubmit();
        }
    }

    function doSubmit() {
        $form.patch(update.url(topic.slug), { invalidateCacheTags: ['admin-topics'] });
    }

    function deleteTopic() {
        router.delete(deleteMethod.url(topic.slug), { invalidateCacheTags: ['admin-topics'] });
    }
</script>

<svelte:head>
    <title>Edit {topic.name} — Admin — Case of the Day</title>
</svelte:head>

<div class="mb-6">
    <Link href={topics()} class="text-sm text-cyan-400 hover:text-cyan-300">&larr; Back to Topics</Link>
</div>

<h1 class="text-2xl font-bold text-gray-100 mb-6">Edit Topic</h1>

<form onsubmit={submit} class="max-w-lg space-y-6">
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-4">
            <div>
                <label for="name" class="block text-sm font-medium text-gray-300 mb-1">Name</label>
                <input
                    id="name"
                    type="text"
                    bind:value={$form.name}
                    class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                />
                {#if $form.errors.name}<p class="text-red-500 text-sm mt-1">{$form.errors.name}</p>{/if}
            </div>

            <div>
                <label for="slug" class="block text-sm font-medium text-gray-300 mb-1">Slug</label>
                <input
                    id="slug"
                    type="text"
                    bind:value={$form.slug}
                    class="block w-full rounded-md border bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm font-mono focus:border-cyan-500 focus:ring-cyan-500 {slugChanged ? 'border-amber-500' : 'border-gray-700'}"
                />
                <p class="text-xs text-gray-500 mt-1">Used in URLs. Lowercase letters, numbers, and hyphens only.</p>
                {#if $form.errors.slug}<p class="text-red-500 text-sm mt-1">{$form.errors.slug}</p>{/if}
            </div>

            <label for="disabled" class="flex items-center gap-3 cursor-pointer">
                <input
                    id="disabled"
                    type="checkbox"
                    bind:checked={$form.disabled}
                    class="h-4 w-4 rounded border-gray-700 text-cyan-600 focus:ring-cyan-500"
                />
                <div>
                    <span class="text-sm font-medium text-gray-300">Disabled</span>
                    <p class="text-xs text-gray-500">Disabled topics are not available for the current year.</p>
                </div>
            </label>

            <label for="not_for_credit" class="flex items-center gap-3 cursor-pointer">
                <input
                    id="not_for_credit"
                    type="checkbox"
                    bind:checked={$form.not_for_credit}
                    class="h-4 w-4 rounded border-gray-700 text-cyan-600 focus:ring-cyan-500"
                />
                <div>
                    <span class="text-sm font-medium text-gray-300">Not for credit</span>
                    <p class="text-xs text-gray-500">Marks this topic as "just for fun" — not counted toward scores.</p>
                </div>
            </label>
        </div>

        <div class="flex items-center gap-3">
            <button
                type="submit"
                disabled={$form.processing || !$form.name.trim()}
                class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 transition-colors"
            >
                {$form.processing ? 'Saving...' : 'Save Changes'}
            </button>
            {#if $form.recentlySuccessful}
                <span class="text-sm text-green-400">Saved.</span>
            {/if}
            <Link href={topics()} class="text-sm text-gray-500 hover:text-gray-300">Cancel</Link>
        </div>
    </form>

    {#if topic.cases_count === 0}
        <div class="max-w-lg mt-10 border-t border-gray-700 pt-6">
            <h2 class="text-lg font-semibold text-red-400 mb-2">Danger Zone</h2>
            <p class="text-sm text-gray-500 mb-4">Permanently delete this topic. This cannot be undone.</p>
            <button
                onclick={() => confirmingDelete = true}
                class="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
            >
                Delete Topic
            </button>
        </div>
    {:else}
        <div class="max-w-lg mt-10 border-t border-gray-700 pt-6">
            <p class="text-sm text-gray-400">This topic has {topic.cases_count} {topic.cases_count === 1 ? 'case' : 'cases'} and cannot be deleted.</p>
        </div>
    {/if}

    <div class="max-w-lg mt-10 border-t border-gray-700 pt-6">
        <h2 class="text-lg font-semibold text-gray-100 mb-5">Activity</h2>
        <ActivityFeed {activity} emptyMessage="No activity for this topic." />
    </div>

    <ConfirmModal
        bind:open={confirmingDelete}
        title="Delete Topic"
        message={`Are you sure you want to delete "${topic.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onconfirm={deleteTopic}
        oncancel={() => confirmingDelete = false}
    />

    <ConfirmModal
        bind:open={confirmingSlugChange}
        title="Change URL Slug"
        message={`You're changing the slug from "${topic.slug}" to "${$form.slug}". This will break any existing links or bookmarks using the old URL.`}
        confirmLabel="Change Slug & Save"
        variant="warning"
        panelClass="warning-glow border border-amber-500/60"
        onconfirm={doSubmit}
        oncancel={() => confirmingSlugChange = false}
    />

<style>
    @keyframes warning-glow {
        0%, 100% {
            box-shadow: 0 0 40px 20px rgba(245, 158, 11, 0.3);
        }
        50% {
            box-shadow: 0 0 60px 30px rgba(245, 158, 11, 0.6);
        }
    }

    :global(.warning-glow) {
        animation: warning-glow 2s ease-in-out infinite;
    }
</style>
